import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { useRBAC } from '../../context/RBACContext';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';

import StatusChip, { memberStatusMap } from '../common/feedback/StatusChip';
import { Role, ApiError } from '../../types';
import { Member } from '../../context/OrganizationContext';
import './utils/compatibility-fix';
import ErrorDisplay from '../common/feedback/ErrorDisplay';
import ConfirmDialog from '../common/dialogs/ConfirmDialog';
import { formatDate } from '../../utils/date-formatter';

interface TeamMembersListProps {
  members: Member[];
  roles?: Role[];
  isLoading?: boolean;
  error?: ApiError | null;
  onInviteMember: () => void;
  onRemoveMember: (memberId: string) => Promise<{ success: boolean; error?: ApiError }>;
  onUpdateMemberRole: (memberId: string, roleId: string) => Promise<{ success: boolean; error?: ApiError }>;
}

// This legacy helper function is no longer needed - we use permissionChecker instead
// Kept for reference only
// const canAssignRole = (role: Role, userPermissions: any[] = []) => { ... };

// Row Actions Cell Component for managing per-row menu
function RowActionsCell({
  member,
  roles,
  onRoleChange,
  onRemove
}: {
  member: Member;
  roles: Role[];
  onRoleChange: (memberId: string, roleId: string) => Promise<{ success: boolean; error?: ApiError }>;
  onRemove: (memberId: string) => void;
}) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editRoleOpen, setEditRoleOpen] = useState(false);
  const open = Boolean(anchorEl);
  const { permissionChecker } = useRBAC();
  const { showSuccess, showError } = useNotification();
  const { user } = useAuth();

  // Check if this member is the current user
  // Note: User type has _id field, Member type has userId field
  const isCurrentUser = user?._id === member.userId;

  // Check if user has role:assign permission
  const hasRoleAssignPermission = permissionChecker?.hasPermission('role:assign') ?? false;

  // If user has role:assign permission, they can assign all roles
  // Otherwise, no roles can be assigned (removed complex legacy logic)
  const assignableRoles = hasRoleAssignPermission ? roles : [];

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setEditRoleOpen(false);
  };

  const handleOpenEditRoleMenu = () => {
    setEditRoleOpen(true);
  };

  const handleCloseEditRoleMenu = () => {
    setEditRoleOpen(false);
  };

  const handleRoleChange = async (roleId: string) => {
    console.log('handleRoleChange called with roleId:', roleId);

    // Find the role using _id or id
    const selectedRole = roles.find(r => r._id === roleId || (r as any).id === roleId);
    console.log('Selected role:', selectedRole);

    // Use userId instead of the composite member.id (which may contain userId_roleId format)
    const userId = member.userId || member.id;
    console.log('Updating role for userId:', userId, 'to roleId:', roleId);

    // Close the menu before making the async call
    handleClose();

    const result = await onRoleChange(userId, roleId);
    console.log('Update result:', result);

    if (result.success) {
      const message = `${member.name}'s role has been updated to ${selectedRole?.name || 'new role'}`;
      console.log('Showing success notification:', message);
      showSuccess(message);
    } else {
      const message = result.error?.message || 'Failed to update member role';
      console.log('Showing error notification:', message);
      showError(message);
    }
  };

  const handleRemoveClick = () => {
    // Use userId instead of the composite member.id
    const userId = member.userId || member.id;
    onRemove(userId);
    handleClose();
  };

  // Check if user has permission to remove OTHER members
  const canRemoveOtherMembers = permissionChecker?.hasPermission('member:remove') ?? false;

  // Check if user has permission to update roles
  const canUpdateRoles = assignableRoles.length > 0;

  // Users can always leave (remove themselves), but may fail backend validation
  const canLeaveTeam = isCurrentUser;

  // Don't render the actions button if user has no available actions
  if (!canUpdateRoles && !canRemoveOtherMembers && !canLeaveTeam) {
    return null;
  }

  return (
    <Box>
      <IconButton
        aria-label="actions"
        aria-controls={`actions-menu-${member.id}`}
        aria-haspopup="true"
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      
      {/* Main Actions Menu */}
      <Menu
        id={`actions-menu-${member.id}`}
        anchorEl={anchorEl}
        open={open && !editRoleOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {canUpdateRoles && (
          <MenuItem onClick={handleOpenEditRoleMenu}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isCurrentUser ? 'Change My Role' : 'Change Role'}</ListItemText>
          </MenuItem>
        )}
        {canRemoveOtherMembers && !isCurrentUser && (
          <MenuItem
            onClick={handleRemoveClick}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Remove Member</ListItemText>
          </MenuItem>
        )}
        {canLeaveTeam && (
          <MenuItem
            onClick={handleRemoveClick}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Leave Team</ListItemText>
          </MenuItem>
        )}
        {!canUpdateRoles && !canRemoveOtherMembers && !canLeaveTeam && (
          <MenuItem disabled>
            <ListItemText>No available actions</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Edit Role Menu */}
      <Menu
        id={`role-menu-${member.id}`}
        anchorEl={anchorEl}
        open={open && editRoleOpen}
        onClose={handleCloseEditRoleMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {assignableRoles.length > 0 ? (
          assignableRoles.map((role) => {
            // Use _id or id, whichever is available
            const roleId = role._id || (role as any).id;
            return (
              <MenuItem
                key={roleId}
                onClick={() => handleRoleChange(roleId)}
                selected={member.role === roleId}
              >
                <ListItemText>{role.name}</ListItemText>
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled>
            <ListItemText>No roles available</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}

const TeamMembersList: React.FC<TeamMembersListProps> = ({
  members,
  roles = [],
  isLoading = false,
  error = null,
  onInviteMember,
  onRemoveMember,
  onUpdateMemberRole
}) => {
  const [memberToRemove, setMemberToRemove] = useState<Member | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const { showSuccess, showError } = useNotification();
  const { permissionChecker } = useRBAC();

  // Check if user has permission to invite members
  const canInviteMembers = permissionChecker?.hasPermission('member:invite') ?? false;

  const handleRemoveMember = (userId: string) => {
    // Find member by userId (not the composite ID)
    const member = members.find(m => m.userId === userId || m.id === userId);
    if (member) {
      setMemberToRemove(member);
      setConfirmRemove(true);
    }
  };

  const confirmRemoveMember = async () => {
    if (memberToRemove) {
      // Use userId instead of composite member.id
      const userId = memberToRemove.userId || memberToRemove.id;
      const result = await onRemoveMember(userId);
      if (result.success) {
        setConfirmRemove(false);
        setMemberToRemove(null);
        showSuccess(`${memberToRemove.name} has been removed from the team`);
      } else {
        showError(result.error?.message || 'Failed to remove member');
      }
    }
  };

  // Define columns for DataGrid - memoized to prevent unnecessary re-renders
  const columns: GridColDef<Member>[] = React.useMemo(() => [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams<Member>) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {params.row.avatarUrl ? (
            <Box
              component="img"
              src={params.row.avatarUrl}
              alt={params.row.name}
              sx={{ width: 32, height: 32, borderRadius: '50%', mr: 1 }}
            />
          ) : (
            <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
          )}
          <span>{params.row.name}</span>
        </Box>
      )
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<Member, string | undefined>) => {
        // Use roleName if available, otherwise look up from roles array
        const roleName = (params.row as any).roleName || roles.find(r => r._id === params.row.role)?.name || params.row.role || 'Unknown Role';
        const isAdmin = roleName.includes('Admin');
        
        return (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {isAdmin ? (
              <AdminPanelSettingsIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
            ) : (
              <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
            )}
            {roleName}
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params: GridRenderCellParams<Member>) => (
        <StatusChip status={params.row.status} statusMap={memberStatusMap} />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Joined',
      width: 120,
      renderCell: (params: GridRenderCellParams<Member>) => {
        return formatDate(params.row.createdAt);
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      width: 80,
      align: 'right',
      renderCell: (params: GridRenderCellParams<Member>) => (
        <RowActionsCell
          member={params.row}
          roles={roles}
          onRoleChange={onUpdateMemberRole}
          onRemove={handleRemoveMember}
        />
      )
    }
  ], [members, roles, onUpdateMemberRole, handleRemoveMember]);

  return (
    <Box>
      {error && (
        <ErrorDisplay
          error={error}
          onClose={() => {}}
          fullWidth
          sx={{ mb: 2 }}
        />
      )}

      {canInviteMembers && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onInviteMember}
          >
            Invite Member
          </Button>
        </Box>
      )}
      
      <DataGrid
        rows={members}
        columns={columns}
        loading={isLoading}
        getRowId={(row: Member) => row.id}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            overflow: 'visible',
          },
          '& .MuiDataGrid-cell': {
            overflow: 'visible',
          },
        }}
        density="standard"
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 10 } },
        }}
        localeText={{
          noRowsLabel: 'No members in this team yet',
          noResultsOverlayLabel: 'No members found',
          MuiTablePagination: {
            labelRowsPerPage: 'Members per page',
          },
        }}
      />
      
      {/* Confirm Remove Dialog */}
      <ConfirmDialog
        open={confirmRemove}
        title="Remove Member"
        content={`Are you sure you want to remove ${memberToRemove?.name} from this team? They will lose all access to the team's resources.`}
        confirmText="Remove"
        cancelText="Cancel"
        onConfirm={confirmRemoveMember}
        onCancel={() => {
          setConfirmRemove(false);
          setMemberToRemove(null);
        }}
        severity="error"
      />
    </Box>
  );
};

export default TeamMembersList;
